/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { SignupRequest } from '../../models/authentication/signup';
import { SignInRequest } from '../../models/authentication/signin';
import { ForgetPasswordRequest } from '../../models/authentication/forget-password';
import { UserProfileAttributeUpdateRequest } from '../../models/profile/profile';
import { CognitoResponseCode, nonCognitoFields, SegmentEvent } from '../../utilities/constants';
import {
  AnalyticsService,
  AuthRedirectService,
  LaasyCookieService,
  LoaderService,
  LogglyLoggerService} from '@laasy/common-layout';
import { Router } from '@angular/router';
import { ToastService } from '../common/toast.service';
import * as authUtils from '../../../modules/auth/auth-utils';
import * as authConsts from '../../../modules/auth/auth-constants';
import { LoginLoaderService } from '../common/loader.service';
import { isProfileCompSubmitSession, isProfileCompViewedOnceSessionKey } from '../../../modules/auth/auth-constants';
import { environment } from 'src/environment/environment';
import { PhoneValidatorService } from 'src/app/modules/home/service/phone-validator.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  nonCognitoFields = nonCognitoFields;
  constructor(
    private cookieService:LaasyCookieService,
    private _router: Router,
    private loaderService: LoaderService,
    private toastService: ToastService,
    private readonly router: Router,
    private authRedirectSrvc: AuthRedirectService,
    private loginLoader:LoginLoaderService,
    private loggly:LogglyLoggerService,
    private analyticsService: AnalyticsService,
    private phoneValidatorService: PhoneValidatorService
  ) {}

  async GetCurrentSession(): Promise<any>{
    try
    {
      this.loaderService.show()
      const response = await Auth.currentSession();
      this.loaderService.hide();
      return response;
    } catch (error: any) {
      this.loaderService.hide()
      return null;
    }
  }

  async GetCurrentUser(): Promise<any> {
    try {
      this.loaderService.show();
      const response = await Auth.currentAuthenticatedUser();
      this.loaderService.hide();
      return response;
    } catch (error: any) {
      this.loggly.ErrorLog(error as Error);
      this.loaderService.hide();
      return null;
    }
  }

  async CognitoUserpoolLogin(request: SignInRequest) {
    try {
      const response = await Auth.signIn(request.email?.toLowerCase(), request.password);
      this.analyticsService.segmentTrackEvent( { email: request.email}, SegmentEvent.loggedIn);
      return response;
    } catch (error) {
      this.analyticsService.segmentTrackEvent( { email: request.email, error: error}, SegmentEvent.loginError);
      this.loggly.ErrorLog(error as Error);
      return error;
    }
  }

  async FederatedAuth(provider: string) {
    const userInfo = JSON.parse(sessionStorage.getItem('ssoSignupUserInfo') || '{}');
    const email = (userInfo?.email && userInfo?.email !== '' )? userInfo?.email : SegmentEvent.ssoUnknownUser;
    this.analyticsService.segmentTrackEvent( { email: email}, SegmentEvent.ssoLoginSignup);
    try {
      let response;
      if (provider === 'Google') {
        response = await Auth.federatedSignIn({
          provider: CognitoHostedUIIdentityProvider.Google
        });
      } else if (provider === 'Apple') {
        response = await Auth.federatedSignIn({
          provider: CognitoHostedUIIdentityProvider.Apple,
        });
      } else {
        response = await Auth.federatedSignIn({ provider: '' as any });
      }
      return response;
    } catch (error) {
      this.analyticsService.segmentTrackEvent( { email: userInfo?.email ?? SegmentEvent.ssoUnknownUser, error: error}, SegmentEvent.ssoLoginSignup);
      this.loggly.ErrorLog(error as Error);
      return error;
    }
  }

  async IdPAuth() {
    this.FederatedAuth('idp');
  }

  IdPAuthWithEntry(entry: string) {
    try {
      window.location.href = this.buildAuthorizeUrl({ entry });
    } catch (error) {
      this.loggly.ErrorLog(error as Error);
    }
  }

  private buildAuthorizeUrl(extraParams: Record<string, string> = {}): string {
    const oauth: any = Auth.configure().oauth;
    if (!oauth) {
      throw new Error('OAuth config missing');
    }
    const { domain, responseType, scope } = oauth;
    const scopeStr = Array.isArray(scope) ? scope.join(' ') : (scope || 'openid email');
    const params = new URLSearchParams({
      redirect_uri: environment.aws_redirectSignIn,
      response_type: responseType,
      client_id: environment.cognitoAppClientId,
      scope: scopeStr,
      ...extraParams
    });
    return `https://${domain}/oauth2/authorize?${params.toString()}`;
  }

  // Note: redirect = false when user is authenticated and moved to auth pages like login,signup,forgotpassword
  async Logout(isGlobal: boolean,redirect:boolean = true) {
    try {
      localStorage.removeItem(isProfileCompSubmitSession.key)
      localStorage.removeItem(isProfileCompViewedOnceSessionKey)
      this.loginLoader.showLoginLoader(false);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const tenant = await this.GetCurrentSession();
      this.cookieService.removeContext();
      this.cookieService.removeUserAPICache();
      authUtils.clearWalletCache()
      await Auth.signOut({global: isGlobal})
      if(redirect)
        this._router.navigateByUrl('/auth/signin');
      return null;
    } catch (error) {
      this.loggly.ErrorLog(error as Error);
      return error;
    }
  }

  async SignUp(request: SignupRequest) {
    try {
      this.loaderService.show();
      request.phoneNumber = this.phoneValidatorService.getCleanPhoneNumber(request.phoneNumber);
      const response = await Auth.signUp({
        username: request.email?.toLowerCase(),
        password: request.password,
        attributes: {
          email: request.email?.toLowerCase(),
          given_name: request.firstName,
          family_name: request.lastName,
          ...(request.phoneNumber ? { phone_number: `${request.phoneNumber}` } : {})
        },
        autoSignIn: {
          enabled: true
        }
      });
      this.loaderService.hide();
      return response;
    } catch (error: any) {
      this.analyticsService.segmentTrackEvent( { email: request.email, error: error}, SegmentEvent.signupError);
      this.loggly.ErrorLog(error as Error);
      this.loaderService.hide();
      return error;
    }
  }

  async SendPasswordChangeConfirmationCode(email: string) {
    try {
      this.loaderService.show();
      const response = await Auth.forgotPassword(email?.toLowerCase());
      this.loaderService.hide();
      return "success";
    } catch (error) {
      this.loggly.ErrorLog(error as Error);
      this.loaderService.hide();
      return error;
    }
  }

  async PasswordChangeVerification(request: ForgetPasswordRequest): Promise<any> {
    try {
      this.loaderService.show();
      const response = await Auth.forgotPasswordSubmit(
        request.email?.toLowerCase(),
        request.verificationCode,
        request.newPassword
      );
      this.loaderService.hide();
      return response;
    } catch (error) {
      this.loggly.ErrorLog(error as Error);
      this.loaderService.hide();
      return error;
    }
  }

  async UpdatePassword(oldPassword: string, newPassword: string) {
    return await Auth.currentAuthenticatedUser()
      .then(async user => {
        return await Auth.changePassword(user, oldPassword, newPassword)
          .then(res => {
            return res;
          })
          .catch(error => {
            this.loggly.ErrorLog(error as Error);
            return error;
          });
      })
      .catch(error => {
        this.loggly.ErrorLog(error as Error);
        return error;
      });
  }

  async UpdateUserAttribute(updateAttributeRequest: UserProfileAttributeUpdateRequest) {
    try {
      const user = await this.GetCurrentUser();
      if (!user) return null;
      if (updateAttributeRequest.attribute == 'email') {
        return CognitoResponseCode.Success;
      } else if (updateAttributeRequest.attribute == 'phoneNumber') {
        return await Auth.updateUserAttributes(user, {
          phone_number: `${this.phoneValidatorService.getCleanPhoneNumber(updateAttributeRequest.value)}`
        });
      } else if (updateAttributeRequest.attribute == 'firstName') {
        return await Auth.updateUserAttributes(user, {
          given_name: updateAttributeRequest.value
        });
      } else if (updateAttributeRequest.attribute == 'lastName') {
        return await Auth.updateUserAttributes(user, {
          family_name: updateAttributeRequest.value
        });
      } else if (nonCognitoFields.some(x => { return x == updateAttributeRequest.attribute })) {
        return 'Non-Cognito-Field';
      }
      return null;
    } catch (error) {
      this.loggly.ErrorLog(error as Error);
      return error;
    }
  }

  redirectToProduct(cardData: any): void {
    this.authRedirectSrvc.startSwitchingProcess(cardData.productUrl, cardData.launchMode);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async updatePassword(userProfile:any): Promise<any> {
    const response = await this.PasswordChangeVerification({
      email: userProfile.email,
      verificationCode: userProfile.otp,
      newPassword: userProfile.pass
    });
    if (response.code === CognitoResponseCode.CodeMismatchException) {
      this.toastService.setToast({
        ...authConsts.error_msg,
        heading: 'Incorrect verification code. Try again!',
        text: authUtils.ErrorMsgDict(userProfile.email, 'invalidcode')
      });
    } else if (response === CognitoResponseCode.Success) {
      return { success: true, email: userProfile.email, password: userProfile.pass };
    } else {
      this.toastService.setToast({
        ...authConsts.error_msg,
        heading: 'Error while changing password',
        text: authUtils.ErrorMsgDict(userProfile.email, '')
      });
    }
    return { success: false };
  }

  iframeSSO(provider: string) {
    try {
      const url = this.buildAuthorizeUrl({ identity_provider: provider });
      // open in new popup window, cognito hosted login
      window.open(url, "_blank", "width=500,height=600");
    } catch (error) {
      this.loggly.ErrorLog(error as Error);
    }

  }
}
